import { useContext, useEffect, useRef, useState, type KeyboardEvent as KeyEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { api } from "../api/client";
import type { Message } from "../api/types";
import { useBackLayer } from "../backstack";
import { celebrate } from "../confetti";
import { HIDDEN, PageShown } from "../pageShown";
import { Btn, toast, useDrawerSlot } from "./Kit";
import { ShellIcon } from "./ShellIcons";

/**
 * The chat page's own question (prototype v2.20 ChatModal): a card in the middle, 440 wide, that pops; on a phone
 * a sheet from the bottom. Drawn on <body> over everything — the page, the side drawer — and hidden with its page
 * when another is shown (pageShown.ts). Esc closes it alone (the 客户详情 under it stays), as do ✕, the shade and
 * a phone's back.
 */
export function ChatModal({
  title,
  sub,
  wide,
  onClose,
  foot,
  children,
}: {
  title: string;
  sub?: ReactNode;
  wide?: boolean;
  onClose: () => void;
  /** Along the bottom: 取消 and the answer. */
  foot?: ReactNode;
  children: ReactNode;
}) {
  const shown = useContext(PageShown);
  const close = useRef(onClose);
  close.current = onClose;
  const onTop = useDrawerSlot();
  useBackLayer(true, () => close.current());
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !onTop()) return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      close.current();
    };
    window.addEventListener("keydown", esc, true);
    return () => window.removeEventListener("keydown", esc, true);
  }, [onTop]);
  return createPortal(
    <div className="wc-modal" role="dialog" aria-modal="true" aria-label={title} style={shown ? undefined : HIDDEN}>
      <div className="wc-modal-scrim" onClick={onClose} />
      <div className={`wc-modal-card${wide ? " wide" : ""}`}>
        <div className="wc-modal-h">
          <div className="wc-modal-t">
            <b>{title}</b>
            {sub && <small>{sub}</small>}
          </div>
          <Btn kind="ghost" size="sm" icon="x" title="关掉" onClick={onClose} />
        </div>
        <div className="wc-modal-b">{children}</div>
        {foot && <div className="wc-modal-f">{foot}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Where a burst of confetti starts: the button just pressed (it has the focus), as 0–1 of the screen. */
export function burstFrom(): { x: number; y: number } | undefined {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement) || el === document.body) return undefined;
  const r = el.getBoundingClientRect();
  if (!r.width) return undefined;
  return { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight };
}

/**
 * The words a customer uses, taps or shows when they have paid. Kept in step
 * with attribution.PaidHintPattern on the API, which computes the same flag
 * for the list; this copy is for the conversation that is open.
 */
const PAID = /已经汇款|汇款了|汇了|转账了|过账|已付|付了|付款了|付款成功|支付了|支付详情|支付成功|收据|receipt|transfer(red| done)|bank(ed)? ?in ?了|banked in|order ?id|deposit slip|交易已?成功|转账成功|汇款成功|(payment|transaction|transfer) (was )?successful|successful(ly)? (payment|transaction|transfer)/i;

export function looksPaid(messages: Message[]): boolean {
  return messages.some((m) => m.direction === "in" && PAID.test(m.text ?? ""));
}

/** The last RM figure in the conversation — usually the order button the customer tapped. */
export function guessAmount(messages: Message[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = /RM\s?([\d,]+(?:\.\d{1,2})?)/i.exec(messages[i]?.text ?? "");
    if (m?.[1]) return m[1].replace(/,/g, "");
  }
  return "";
}

/** An order id the customer or a colleague typed, if any. */
export function guessReference(messages: Message[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = /order\s*id\s*[:：]?\s*([A-Za-z0-9-]{3,})/i.exec(messages[i]?.text ?? "");
    if (m?.[1]) return m[1];
  }
  return "";
}

interface Props {
  waID: string;
  /** The customer, for the line under the title. */
  who: string;
  messages: Message[];
  onDone: () => void;
  /** note: what to tell the operator on the way out, empty when there is nothing. */
  onClose: (note?: string) => void;
}

/**
 * Recording a sale, as one question: how much, and which order. Both are
 * guessed from the conversation and can be corrected. The amount is what makes
 * the sale count in the report and what Meta can bid on; the reference is what
 * stops the same sale being counted twice.
 *
 * Only the amount is required. A bank transfer arrives here as a photo of a
 * receipt — no order, often no legible reference — and demanding one turned the
 * form into a wall: the operator typed the amount, pressed 记下, got a red line
 * asking for a number they did not have, and the sale stayed unrecorded. An
 * empty reference becomes the day itself on the server, which still collapses a
 * double-click into one sale; the one case it cannot tell apart, a second sale
 * to the same customer on the same day, comes back as `duplicate` and is said
 * out loud here.
 *
 * Recorded, it says so in gold with a burst of confetti from 记下 (prototype chat.js recordSale).
 */
export default function SaleDialog({ waID, who, messages, onDone, onClose }: Props) {
  const [amount, setAmount] = useState(() => guessAmount(messages));
  const [reference, setReference] = useState(() => guessReference(messages));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    const value = parseFloat(amount);
    if (!(value > 0)) {
      setErr("填实际收到的金额");
      return;
    }
    const from = burstFrom();
    setBusy(true);
    setErr("");
    try {
      const res = await api.recordSale(waID, value, "MYR", reference.trim());
      // A duplicate is a 200. Left unsaid it reads as success, and the operator
      // walks away from a second sale that was never recorded.
      if (res.status === "duplicate") {
        setErr("这一笔已经记过了。是第二笔的话，填上单号再记");
        return;
      }
      onDone();
      toast(`记了一笔成交 · RM${value.toFixed(2)}`, "gold");
      celebrate("small", from ?? { x: 0.5, y: 0.6 });
      // "recorded" means the sale is ours but the ad platform will not hear
      // about it — the customer never clicked an ad, or no consent was
      // captured. Said once, on the way out: it changes nothing the operator
      // must do, and hiding it would let the shop believe every recorded sale
      // is teaching Meta something.
      onClose(res.status === "recorded" ? (res.detail ?? "") : "");
    } catch (e) {
      setErr("没记上：" + (e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const enter = (e: KeyEvent) => e.key === "Enter" && !e.nativeEvent.isComposing && void submit();

  return (
    <ChatModal
      title="记一笔成交"
      sub={`${who} · 客户付了款，记金额和单号`}
      onClose={() => onClose()}
      foot={
        <>
          <Btn kind="ghost" onClick={() => onClose()}>
            取消
          </Btn>
          <Btn kind="primary" icon="check" disabled={busy} onClick={() => void submit()}>
            {busy ? "记录中…" : "记下"}
          </Btn>
        </>
      }
    >
      <label className="wc-field">
        <span>收到多少（RM）</span>
        <span className="wc-input">
          <b>RM</b>
          <input
            type="text"
            autoFocus
            inputMode="decimal"
            value={amount}
            placeholder="例如 149"
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => {
              setAmount(e.target.value.replace(/[^\d.]/g, ""));
              setErr("");
            }}
            onKeyDown={enter}
          />
        </span>
      </label>
      <label className="wc-field">
        <span>订单号或银行参考号</span>
        <span className="wc-input">
          <input
            type="text"
            autoComplete="off"
            value={reference}
            placeholder="没有就留空"
            onChange={(e) => {
              setReference(e.target.value);
              setErr("");
            }}
            onKeyDown={enter}
          />
        </span>
      </label>
      {err ? (
        <p className="wc-err-l">
          <ShellIcon name="alert" size={14} />
          {err}
        </p>
      ) : (
        <p className="wc-why">没有单号也能记；同一天第二笔才要单号</p>
      )}
    </ChatModal>
  );
}
